import { useState, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const RESUME_KEY = "winfield_last_reservation";

/**
 * ResumeBanner
 * A slim, dismissible pill that nudges returning buyers back to their
 * reservation when they have one stored in localStorage. Rendered fixed at the
 * very top of public pages (Landing), ABOVE the fixed navbar. It publishes its
 * own height to the `--wf-banner-h` CSS variable so the navbar and page content
 * can offset themselves below it.
 */
export function ResumeBanner() {
  const { lang } = useLanguage();
  const [hidden, setHidden] = useState(false);
  const ref = useRef(null);
  const [data] = useState(() => {
    try {
      const raw = window.localStorage.getItem(RESUME_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.id && parsed.contract_number) return parsed;
    } catch { /* ignore */ }
    return null;
  });

  const visible = !!data && !hidden;
  const preSignature = data && ["draft", "contacted", "signed"].includes(data.status);
  const target = preSignature ? `/reserve?id=${data?.id}` : `/r/${data?.id}`;

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (visible && ref.current) {
      const setH = () => root.style.setProperty("--wf-banner-h", `${ref.current.offsetHeight}px`);
      setH();
      window.addEventListener("resize", setH);
      return () => {
        window.removeEventListener("resize", setH);
        root.style.setProperty("--wf-banner-h", "0px");
      };
    }
    root.style.setProperty("--wf-banner-h", "0px");
    return undefined;
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    setHidden(true);
    try { window.localStorage.removeItem(RESUME_KEY); } catch { /* ignore */ }
  };

  return (
    <div
      ref={ref}
      data-testid="resume-banner"
      className="fixed top-0 left-0 right-0 z-[60] bg-[#0A0A0F] text-white border-b border-[#1F1F26]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <FileText size={14} className="shrink-0 opacity-80" />
          <p className="text-xs sm:text-sm min-w-0 truncate">
            {lang === "sr" ? "Otvorena rezervacija:" : "You have an open reservation:"}{" "}
            <span className="font-mono font-semibold">{data.contract_number}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to={target}
            data-testid="resume-banner-link"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#0A0A0F] text-xs sm:text-sm font-semibold hover:bg-[#FBF8F1] transition-colors"
          >
            {lang === "sr" ? "Otvori" : "Open"} <ArrowRight size={12} />
          </Link>
          <button
            type="button"
            onClick={dismiss}
            data-testid="resume-banner-dismiss"
            aria-label={lang === "sr" ? "Zatvori" : "Dismiss"}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-white/10 transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
